import {
  addNode, removeNode, moveNode, reparentNode, duplicateNode, updateProps,
  type BlockDocument, type EditorContext, type OperationResult,
} from './document.ts';

/**
 * Editor state.
 *
 * ── ONE REDUCER, NOT A STATE LIBRARY ─────────────────────────────────────────────────
 *
 * Every structural change goes through here, which is what keeps document mutation in one
 * reviewable place instead of spread across a dozen components. React's own useReducer is
 * enough: an Event template is a handful of nodes, the state is local to one screen, and
 * adding a global store would be infrastructure this does not need.
 *
 * ── DIRTY IS COMPUTED, NOT TRACKED ───────────────────────────────────────────────────
 *
 * `savedDocument` is what the server last accepted. Dirtiness is a comparison against it,
 * so it cannot drift out of step with reality the way a boolean somebody forgot to reset
 * would — and undoing an edit by hand correctly makes the editor clean again.
 */

export interface EditorState {
  document: BlockDocument;
  /** What the server last accepted. The baseline for "are there unsaved changes". */
  savedDocument: BlockDocument;
  /** The draft version the server gave us, for optimistic concurrency. */
  savedVersion: number | null;
  context: EditorContext;
  selectedNodeId: string | null;
  /** Set by a refused operation; cleared on the next successful one. */
  lastRefusal: { reason: string; message: string } | null;
}

export type EditorAction =
  | { type: 'SELECT_NODE'; nodeId: string | null }
  | { type: 'UPDATE_PROPS'; nodeId: string; props: Record<string, unknown> }
  | { type: 'ADD_NODE'; parentId: string; component: string; index?: number }
  | { type: 'REMOVE_NODE'; nodeId: string }
  | { type: 'MOVE_NODE'; nodeId: string; direction: 'up' | 'down' }
  | { type: 'REPARENT_NODE'; nodeId: string; newParentId: string; index?: number }
  | { type: 'DUPLICATE_NODE'; nodeId: string }
  // `context` travels WITH the document, because the two are inseparable: a document
  // loaded for a global section must be validated and added to at the global_section
  // context, and an editor whose context lagged behind its document offered blocks in
  // the palette that the reducer then refused. It did exactly that until task 13.
  | {
    type: 'REPLACE_DOCUMENT';
    document: BlockDocument;
    savedVersion?: number | null;
    context?: EditorContext;
  }
  | { type: 'MARK_SAVED'; document: BlockDocument; savedVersion: number | null }
  | { type: 'DISMISS_REFUSAL' };

export function initialEditorState(
  document: BlockDocument, context: EditorContext, savedVersion: number | null,
): EditorState {
  return {
    document,
    savedDocument: document,
    savedVersion,
    context,
    selectedNodeId: document?.root?.id ?? null,
    lastRefusal: null,
  };
}

/** Apply an operation result: adopt the new document, or record why it was refused. */
function applied(state: EditorState, result: OperationResult): EditorState {
  if (!result.ok) {
    return {
      ...state,
      lastRefusal: { reason: result.reason ?? 'refused', message: result.message ?? 'Not allowed.' },
    };
  }
  return {
    ...state,
    document: result.document,
    selectedNodeId: result.newNodeId ?? state.selectedNodeId,
    lastRefusal: null,
  };
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'SELECT_NODE':
      return { ...state, selectedNodeId: action.nodeId };

    case 'UPDATE_PROPS':
      return applied(state, updateProps(state.document, action.nodeId, action.props));

    case 'ADD_NODE':
      return applied(state, addNode(state.document, {
        parentId: action.parentId, component: action.component,
        context: state.context, index: action.index,
      }));

    case 'REMOVE_NODE': {
      const next = applied(state, removeNode(state.document, action.nodeId));
      // A selection pointing at a node that no longer exists would leave the inspector
      // describing something that is gone.
      if (next.document !== state.document && state.selectedNodeId === action.nodeId) {
        return { ...next, selectedNodeId: next.document.root?.id ?? null };
      }
      return next;
    }

    case 'MOVE_NODE':
      return applied(state, moveNode(state.document, action.nodeId, action.direction));

    case 'REPARENT_NODE':
      return applied(state, reparentNode(
        state.document, action.nodeId, action.newParentId, action.index));

    case 'DUPLICATE_NODE':
      return applied(state, duplicateNode(state.document, action.nodeId));

    case 'REPLACE_DOCUMENT':
      // Used when the server hands back a newer draft after a conflict. The incoming
      // document becomes both the working copy and the baseline: it IS what the server
      // holds, so the editor is clean until somebody edits again.
      return {
        ...state,
        document: action.document,
        savedDocument: action.document,
        savedVersion: action.savedVersion ?? state.savedVersion,
        context: action.context ?? state.context,
        selectedNodeId: action.document?.root?.id ?? null,
        lastRefusal: null,
      };

    case 'MARK_SAVED':
      return {
        ...state,
        savedDocument: action.document,
        savedVersion: action.savedVersion,
        lastRefusal: null,
      };

    case 'DISMISS_REFUSAL':
      return { ...state, lastRefusal: null };

    default:
      return state;
  }
}

/** Are there unsaved changes? Compared, never tracked. */
export function isDirty(state: EditorState): boolean {
  return JSON.stringify(state.document) !== JSON.stringify(state.savedDocument);
}
